import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  SkeletonText,
  Stack,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import IconArrowLeftRight from "assets/icons/icon-arrow-left-right.svg?react";
import IconArrowNext from "assets/icons/icon-arrow-next.svg?react";
import IconEye from "assets/icons/icon-eye.svg?react";
import ButtonEmailPreview from "components/ButtonEmailPreview";
import ButtonBack from "components/ui/ButtonBack";
import ButtonRemove from "components/ui/ButtonRemove";
import CompanyHeader from "components/ui/CompanyHeader";
import DateTime from "components/ui/DateTime";
import FixedBar from "components/ui/FixedBar";
import HeaderBarInternal from "components/ui/HeaderBarInternal";
import InfoList from "components/ui/InfoList";
import {
  LinkContentPreview,
  LinkNewsletterPreview,
} from "features/emails/config";
import { useMessages } from "hooks/useMessages";
import { useResource } from "hooks/useResource";
import axios from "lib/axios";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

interface ContentItem {
  id: string;
  title: string;
  subject: string;
  createdAt: string;
  contentGroup: {
    id: string;
    name: string;
    type: string;
  };
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
}

export default function PageNewsletterReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { onSuccess, onError } = useMessages({
    success: "Newsletter revisada com sucesso!",
    error: "Erro ao revisar newsletter. Tente novamente.",
  });

  const [removedContentIds, setRemovedContentIds] = useState<string[]>([]);

  const { data: newsletter, isLoading } = useResource({
    id: id as string,
    resourceName: "newsletter",
    path: "newsletters/review",
  }) as { data: any; isLoading: boolean }; // eslint-disable-line @typescript-eslint/no-explicit-any

  const reviewMutation = useMutation({
    mutationFn: async (data: { removedContentIds: string[] }) => {
      const response = await axios.post(`newsletters/review/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      onSuccess();
      queryClient.invalidateQueries({ queryKey: ["newsletters"] });
      queryClient.invalidateQueries({ queryKey: ["newsletters-review"] });
      navigate("/disparos-de-newsletter/aguardando-revisao");
    },
    onError: (error: unknown) => {
      onError((error as any)?.response?.data?.message); // eslint-disable-line @typescript-eslint/no-explicit-any
    },
  });

  const handleRemoveContent = (contentId: string) => {
    setRemovedContentIds((prev) => {
      if (prev.includes(contentId)) {
        return prev.filter((id) => id !== contentId);
      } else {
        return [...prev, contentId];
      }
    });
  };

  const onSubmit = () => {
    reviewMutation.mutate({ removedContentIds });
  };

  const isContentRemoved = (contentId: string) =>
    removedContentIds.includes(contentId);

  return (
    <>
      <HeaderBarInternal
        title="Revisar Newsletter"
        backTo="/disparos-de-newsletter/aguardando-revisao"
      />

      <Divider />

      <Container variant="internal" flexDirection="column" mb="120px">
        <SkeletonText isLoaded={!isLoading}>
          {newsletter && (
            <>
              <Card>
                <CardHeader>
                  <Flex alignItems="center">
                    <Stack spacing="5px" alignItems="flex-start" mr="25px">
                      <Heading as="h3" size="h3">
                        {newsletter.subject}
                      </Heading>
                      <Tag colorScheme="orange">Aguardando Revisão</Tag>
                    </Stack>
                    <ButtonEmailPreview
                      url={LinkNewsletterPreview(newsletter)}
                    />
                  </Flex>
                </CardHeader>
                <CardBody>
                  <InfoList
                    mt="10px"
                    ml={0}
                    justifyContent="flex-start"
                    spacing="35px"
                  >
                    <Box>
                      <Text as="span">
                        <small>Assunto do e-mail</small>
                        {newsletter.subject}
                      </Text>
                    </Box>

                    <Box>
                      <Text as="span">
                        <small>Criado em</small>
                        <DateTime date={newsletter.createdAt} noBreak />
                      </Text>
                    </Box>

                    <Box>
                      <Text as="span">
                        <small>Edição</small>
                        {newsletter.edition}ª edição
                      </Text>
                    </Box>
                  </InfoList>
                </CardBody>
              </Card>

              <Card mt="30px">
                <CardHeader>
                  <Heading as="h3" size="h3">
                    Lista
                    <Text as="span"></Text>
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Stack spacing="50px">
                    <Box>
                      <CompanyHeader
                        company={newsletter.list.company}
                        description=""
                      />
                      <Table mt="23px">
                        <Tbody>
                          <Tr key={newsletter.list.id}>
                            <Td>{newsletter.list.name}</Td>
                            <Td>
                              {newsletter.list.isNewsletter && (
                                <Tag colorScheme="green">Newsletter</Tag>
                              )}
                            </Td>
                            <Td w="600px">
                              <HStack spacing="8px" flexWrap="wrap">
                                {newsletter.list.tags.map(
                                  (
                                    tag: any // eslint-disable-line @typescript-eslint/no-explicit-any
                                  ) => (
                                    <Tag
                                      key={`tag-${tag.id}`}
                                      colorScheme={tag.colorScheme}
                                    >
                                      {tag.name}
                                    </Tag>
                                  )
                                )}
                              </HStack>
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </Box>
                  </Stack>
                </CardBody>
              </Card>

              <Card mt="30px">
                <CardHeader>
                  <Heading as="h3" size="h3">
                    Reports da Newsletter
                    <Text as="span">
                      Revise e remova conteúdos conforme necessário
                    </Text>
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>Modelo</Th>
                        <Th>Título</Th>
                        <Th>Criado por</Th>
                        <Th>Enviado em</Th>
                        <Th w="120px">Ações</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {newsletter.contents.map((content: ContentItem) => (
                        <Tr
                          key={content.id}
                          bgColor={
                            isContentRemoved(content.id)
                              ? "gray.50"
                              : "transparent"
                          }
                        >
                          <Td opacity={isContentRemoved(content.id) ? 0.5 : 1}>
                            <Tag colorScheme="blue">
                              {content.contentGroup.name}
                            </Tag>
                          </Td>
                          <Td opacity={isContentRemoved(content.id) ? 0.5 : 1}>
                            <Text fontWeight="medium">{content.title}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {content.subject}
                            </Text>
                          </Td>
                          <Td opacity={isContentRemoved(content.id) ? 0.5 : 1}>
                            {content.createdBy.name}
                          </Td>
                          <Td opacity={isContentRemoved(content.id) ? 0.5 : 1}>
                            <DateTime date={content.createdAt} />
                          </Td>
                          <Td>
                            <ButtonGroup>
                              <IconButton
                                aria-label="Visualizar"
                                size="sm"
                                icon={<IconEye />}
                                as={Link}
                                target="_blank"
                                to={LinkContentPreview(content)}
                              />
                              {isContentRemoved(content.id) ? (
                                <IconButton
                                  aria-label="Desfazer remoção"
                                  size="sm"
                                  icon={<IconArrowLeftRight />}
                                  colorScheme="green"
                                  variant="outline"
                                  onClick={() =>
                                    handleRemoveContent(content.id)
                                  }
                                />
                              ) : (
                                <ButtonRemove
                                  onClick={() =>
                                    handleRemoveContent(content.id)
                                  }
                                  message="Tem certeza que deseja remover este conteúdo da newsletter?"
                                />
                              )}
                            </ButtonGroup>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </CardBody>
              </Card>
            </>
          )}
        </SkeletonText>
      </Container>

      <FixedBar>
        <ButtonBack
          as="button"
          onClick={() => navigate("/disparos-de-newsletter/aguardando-revisao")}
        >
          Voltar
        </ButtonBack>

        <HStack spacing="50px">
          <Text fontSize="sm" color="gray.600">
            {removedContentIds.length > 0 &&
              `${removedContentIds.length} conteúdo(s) marcado(s) para remoção`}
          </Text>

          <ButtonGroup>
            <Button
              variant="primary"
              rightIcon={<IconArrowNext />}
              onClick={onSubmit}
              isLoading={reviewMutation.isPending}
              isDisabled={reviewMutation.isPending || (newsletter && removedContentIds.length >= newsletter.contents.length) }
            >
              Confirmar Revisão
            </Button>
          </ButtonGroup>
        </HStack>
      </FixedBar>
    </>
  );
}
